import { io, Socket } from "socket.io-client";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { SyncService } from "../domain/services/SyncService";
import { apiURL } from "../Config";
import { ResponseMessageObj } from "../types/ApiResults";
import { ResponseMessage } from "../domain/enums/common/ResponseMessageEnum";
import { SynchronizationEvent } from "../domain/enums/common/SynchronizationEventEnum";
import { userServiceFactory } from "./UserServiceImpl";

export class SyncServiceImpl implements SyncService {
    private static _instance: SyncService;
    private socket: Socket | undefined;
    private readonly serverUrl = apiURL;

    public readonly notification$ = new Subject<ResponseMessageObj>();
    public readonly refetchRoles$ = new Subject<void>();

    private constructor() { }

    public static getInstance(): SyncService {
        if (!SyncServiceImpl._instance) {
            SyncServiceImpl._instance = new SyncServiceImpl();
        }
        return SyncServiceImpl._instance;
    }

    public connect(): void {
        if (this.socket?.connected) {
            console.warn("WebSocket service is already connected.");
            return;
        }

        console.log("Attempting to connect to WebSocket server...");

        const token = userServiceFactory().getToken();

        this.socket = io(this.serverUrl, {
            autoConnect: true,
            transports: ["websocket"],
            withCredentials: true,
            auth: { token },
        });

        this.setupMessageHandlers();
    }

    public disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = undefined;
            console.log("WebSocket disconnected.");
        }
    }

    private setupMessageHandlers(): void {
        if (!this.socket) return;

        this.socket.on("connect", () => {
            console.log(
                "WebSocket successfully connected. SID:",
                this.socket?.id
            );
        });

        this.socket.on("disconnect", (reason) => {
            console.warn("WebSocket disconnected. Reason:", reason);
        });

        this.socket.on("connect_error", (error) => {
            console.error("WebSocket connection error:", error.message);
            this.notification$.next({
                id: uuidv4(),
                messageType: ResponseMessage.DANGER,
                messageKey: "errors.webSocketConnectionFailed",
            });
        });

        this.socket.on(
            SynchronizationEvent.FEATURE_LIST_UPDATED,
            (data: any) => {
                this.notification$.next({
                    id: uuidv4(),
                    messageType: ResponseMessage.SUCCESS,
                    messageKey: data.data.messageKey,
                    messageArgs: data.data.messageArgs,
                });
                this.refetchRoles$.next();
            }
        );

        this.socket.on(
            SynchronizationEvent.ROLE_UPDATED,
            (data: { messageKey: string; username: string }) => {
                console.log('Received "ROLE_UPDATED" event:', data);

                this.notification$.next({
                    id: uuidv4(),
                    messageType: ResponseMessage.SUCCESS,
                    messageKey: data.messageKey,
                    messageArgs: { user: data.username },
                });
            }
        );
    }
}

export default function syncServiceFactory(): SyncService {
    return SyncServiceImpl.getInstance();
}
